import { X } from '../../lib/icons.tsx';
import { swatchVar } from '../../../shared/palette.ts';
import type { CategoryColour } from '../../../shared/api.ts';

/**
 * "Showing 12 of 62 decks", one chip per set filter, and Clear all — the row,
 * with nothing in it that knows what is being filtered.
 *
 * Split out of `ActiveFilterRow` in Run 9, which is now the decks page's thin
 * wrapper around it: that component derives its chips from `DeckFilterState`,
 * and the Library derives a different set from a different state, but the row
 * itself — the wrap behaviour, the divider that vanishes on a phone, the ×'s
 * hit area and its announcement — is one thing and stays one thing.
 *
 * It renders nothing at all when nothing is filtered rather than sitting there
 * empty, because a permanent strip saying "Showing 62 of 62" is noise that
 * teaches people to stop reading the one line that matters.
 */

/** The shape a chip has to be, whatever page built it. `axis` is the caller's
 *  own union, carried through so `onClearAxis` stays typed end to end. */
export type FilterChipData<A extends string> = {
  axis: A;
  /** The axis name, ending in a colon: "Status:". */
  label: string;
  /** What it is set to, in the user's words. */
  value: string;
  /** A TOKEN NAME behind the chip's dot (`accent`, `lime`…), or null for the
   *  default muted dot. Never a colour literal. */
  dot: string | null;
  /** What the × button announces it will do. */
  clearLabel: string;
};

type Props<A extends string> = {
  chips: readonly FilterChipData<A>[];
  /** "Showing 12 of 62 decks" — built by the caller, whose noun it is. */
  showing: string;
  onClearAxis: (axis: A) => void;
  onClearAll: () => void;
};

export default function FilterChipRow<A extends string>({ chips, showing, onClearAxis, onClearAll }: Props<A>) {
  if (chips.length === 0) return null;
  return (
    <div className="active-filters" role="region" aria-label="Active filters">
      <span className="active-filters__count">{showing}</span>
      <span className="active-filters__divider" aria-hidden="true" />
      {chips.map((chip) => (
        <Chip key={chip.axis} chip={chip} onClear={() => onClearAxis(chip.axis)} />
      ))}
      <button type="button" className="active-filters__clear" onClick={onClearAll}>
        Clear all
      </button>
    </div>
  );
}

function Chip<A extends string>({ chip, onClear }: { chip: FilterChipData<A>; onClear: () => void }) {
  return (
    <span className="filter-chip">
      {/* The dot's colour is a TOKEN NAME from the chip data, never a literal:
          the accent, or a category's swatch from the registry. */}
      <span
        className="filter-chip__dot"
        style={
          chip.dot
            ? { background: chip.dot === 'accent' ? 'var(--accent)' : swatchVar(chip.dot as CategoryColour) }
            : undefined
        }
        aria-hidden="true"
      />
      <span className="filter-chip__label">{chip.label}</span>
      <span className="filter-chip__value">{chip.value}</span>
      <button type="button" className="filter-chip__clear" aria-label={chip.clearLabel} onClick={onClear}>
        <X size={13} />
      </button>
    </span>
  );
}
